import { Observable, Subject } from 'rxjs';
import { AutoLog } from '../decorators/auto-log.decorator';
import { CommonUtils } from '../utils/common.utils';
import { StringUtils } from '../utils/string.util';
import { AUTO_LOG_CATEGORY_FOR_LIB } from '../variables';

/**
 * App storage operation interface
 *
 * @export
 * @interface StorageOperation
 */
export interface StorageOperation {
  setItem<T>(key: string, value: T): void;
  getItem<T>(key: string): T | null;
  removeItem(key: string): void;
  clear(): void;
  notifyChange(event: StorageEvent): void;
}

/**
 * Abstract class for StorageOperation interface basic implementations.
 *
 * @export
 * @abstract
 * @class StorageBase
 * @implements {StorageOperation}
 */
export abstract class StorageBase implements StorageOperation {
  /**
   * Subject for storage change notification.
   */
  protected _change$ = new Subject<StorageEvent>();

  constructor(protected _storage: Storage) {}

  /**
   * Getter for subject of storage change notification.
   */
  get change$(): Observable<StorageEvent> {
    return this._change$.asObservable();
  }

  /**
   * Set item to target storage.
   *
   * @template T
   * @param {string} key
   * @param {T} value
   * @memberof StorageBase
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  setItem<T>(key: string, value: T): void {
    this._storage.setItem(key, JSON.stringify(value));
    const storageEvent: StorageEvent = new StorageEvent('storage', {
      key: key,
      newValue: value as any,
      oldValue: null,
      storageArea: localStorage,
      url: window.location.href,
    });
    this._change$.next(storageEvent);
  }

  /**
   * Get item from target storage.
   *
   * @template T
   * @param {string} key
   * @return {*}  {(T | null)}
   * @memberof StorageBase
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  getItem<T>(key: string): T | null {
    const value = this._storage.getItem(key);

    if (CommonUtils.isNullOrUndefined(value)) {
      return null;
    }

    return StringUtils.isJSON(value as string)
      ? JSON.parse(value as string)
      : value;
  }

  /**
   * Remove item from target storage.
   *
   * @param {string} key
   * @memberof StorageBase
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  removeItem(key: string): void {
    this._storage.removeItem(key);
  }

  /**
   * Clean all data in target storage.
   *
   * @memberof StorageBase
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  clear(): void {
    this._storage.clear();
  }

  /**
   * Emit change notifications.
   *
   * @param {StorageEvent} event
   * @memberof StorageBase
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  notifyChange(event: StorageEvent): void {
    this._change$.next(event);
  }
}
